import React, { useState } from 'react';
import {
  hostPresentsDocuments,
  hostSharesLinks,
  openDocumentUrl,
  shareLinkInHost,
} from '../../../utils/documentsHostBridge';
import './ProposalLinkActions.css';

/**
 * A proposal's online-link controls in the Documents list: open the page,
 * copy the link, or turn it off. (Sending is the estimate's: a proposal
 * goes to the customer as the PDF attached to an estimate send.) A link that is off says so; it
 * can't be turned back on — generate the proposal again for a fresh one.
 *
 * @param {object} props
 * @param {object} props.doc               the `proposalDocs` entry
 * @param {boolean|undefined} props.enabled undefined while the status loads
 * @param {(token: string) => Promise<void>} props.onRevoke
 * @param {string|null} [props.note]       a line above the buttons ("Sent with the estimate …")
 * @param {React.ReactNode} [props.children] more controls, before Turn off link
 */
export default function ProposalLinkActions({
  doc,
  enabled,
  onRevoke,
  note = null,
  children = null,
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!doc.shareToken || !doc.shareUrl) return null;

  const stop = (e) => e.stopPropagation();

  const copy = async (e) => {
    stop(e);
    // Inside the iOS app the share sheet does what "copy" does and more.
    if (shareLinkInHost(doc.shareUrl, doc.name)) return;
    try {
      await navigator.clipboard.writeText(doc.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('[proposal links] copy failed:', err);
    }
  };

  const turnOff = async (e) => {
    stop(e);
    if (
      !window.confirm(
        'Turn off this proposal link? The customer will no longer be able to open the page or the 3D walkthrough.'
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await onRevoke(doc.shareToken);
    } catch (err) {
      console.error('[proposal links] turn off failed:', err);
      window.alert(`Could not turn off the link: ${err?.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  if (enabled === false) {
    return (
      <div className="proposal-link-actions" onClick={stop}>
        <span className="proposal-link-actions__off">Link off</span>
      </div>
    );
  }

  return (
    <div className="proposal-link-actions" onClick={stop}>
      {note && <p className="proposal-link-actions__note">{note}</p>}
      <a
        className="proposal-link-actions__btn"
        href={doc.shareUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          stop(e);
          // A WKWebView opens nothing for target="_blank".
          if (hostPresentsDocuments()) {
            e.preventDefault();
            openDocumentUrl(doc.shareUrl, {
              name: doc.name,
              mimeType: 'text/html',
            });
          }
        }}
      >
        Open page
      </a>
      <button
        type="button"
        className="proposal-link-actions__btn"
        onClick={copy}
      >
        {copied ? 'Copied' : hostSharesLinks() ? 'Share link' : 'Copy link'}
      </button>
      {children}
      <button
        type="button"
        className="proposal-link-actions__btn proposal-link-actions__btn--danger"
        onClick={turnOff}
        disabled={busy || enabled === undefined}
      >
        {busy ? 'Turning off…' : 'Turn off link'}
      </button>
    </div>
  );
}
